import { useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Logo } from '@/components/common/Logo';
import { scrollToTarget } from '@/lib/smooth-scroll';

const navLinks = [
  { key: 'nav.audience', href: '#audience' },
  { key: 'nav.pricing', href: '#pricing' },
  { key: 'nav.faq', href: '#faq' },
];

export function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const openComingSoon = () => { setIsMobileMenuOpen(false); navigate('/coming-soon'); };
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') { navigate('/' + href); return; }
    const target = document.querySelector<HTMLElement>(href);
    if (target) scrollToTarget(target, { offset: -80 });
  };

  return (
    <header
      ref={navRef}
      className={cn(
        'fixed top-0 left-0 w-full z-50 transition-[background-color,border-color,backdrop-filter,box-shadow,padding] duration-200',
        isScrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-border/80 py-3 shadow-subtle'
          : 'bg-transparent py-5'
      )}
    >
      <div className="container mx-auto flex items-center justify-between">
        {/* Animated Vector Logo */}
        <Logo className="navbar-brand" />

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-xs font-mono font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              {t(link.key)}
            </a>
          ))}
        </nav>

        {/* Actions & Toggles */}
        <div className="hidden lg:flex items-center gap-2.5">
          <LanguageSwitcher />
          <ThemeToggle />
          <Button onClick={openComingSoon} variant="primary" size="sm" className="ml-1 font-mono text-xs">
            {t('nav.cta')}
          </Button>
        </div>

        {/* Mobile Hamburger Toggle */}
        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
          <button
            className="p-2 rounded-btn text-foreground hover:bg-muted/80 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t('nav.menu')}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-background/98 backdrop-blur-2xl border-b border-border py-6 px-6 shadow-2xl flex flex-col gap-4">
          <nav className="flex flex-col gap-3 font-mono text-xs">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-foreground hover:text-brand py-1 transition-colors"
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {t(link.key)}
              </a>
            ))}
          </nav>
          <div className="flex items-center justify-between pt-4 border-t border-border/60">
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            <Button onClick={openComingSoon} variant="primary" size="sm" className="flex-1 ml-3 font-mono text-xs">
              {t('nav.cta')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
